import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useLang, uiText } from '../../utils/i18n';

const ContactUs = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { lang } = useLang();
    const [page, setPage] = useState(null);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchPage = async () => {
            try {
                const { data } = await axios.get('/api/v1/contact-page');
                setPage(data.page);
            } catch (error) {
                console.error('Failed to load Contact page', error);
            }
        };
        fetchPage();
    }, []);

    const submitHandler = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || !message.trim()) {
            enqueueSnackbar("Please fill in your name, email, and message", { variant: "warning" });
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post('/api/v1/contact', { name, email, phone, message });
            enqueueSnackbar(data.message, { variant: "success" });
            setName(""); setEmail(""); setPhone(""); setMessage("");
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to send your message. Please try again.", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    if (!page) return <BackdropLoader />;

    const displayHeroTitle = lang === 'zh' && page.heroTitleZh ? page.heroTitleZh : page.heroTitle;
    const displayHeroSubtitle = lang === 'zh' && page.heroSubtitleZh ? page.heroSubtitleZh : page.heroSubtitle;
    const displayAddress = lang === 'zh' && page.addressZh ? page.addressZh : page.address;
    const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(page.address)}&output=embed`;
    const mapDirectionsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(page.address)}`;

    return (
        <>
            <MetaData title="Contact Us | 1313 Mart" />
            {submitting && <BackdropLoader />}

            <main className="w-full sm:w-[90%] m-auto mt-16 sm:mt-8 mb-12 px-3 sm:px-0">

                <div className="bg-primary-green rounded-lg px-6 sm:px-12 py-12 text-white text-center mb-10">
                    <h1 className="text-3xl sm:text-4xl font-bold mb-3">{displayHeroTitle}</h1>
                    <p className="text-sm sm:text-base opacity-90 max-w-2xl mx-auto">{displayHeroSubtitle}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">{uiText('sendUsMessage', lang)}</h2>
                        <form onSubmit={submitHandler} className="flex flex-col gap-4">
                            <input type="text" placeholder={uiText('yourNamePlaceholder', lang)} value={name} onChange={(e) => setName(e.target.value)} className="border rounded px-3 py-2.5 text-sm focus:outline-none focus:border-primary-blue" required />
                            <input type="email" placeholder={uiText('yourEmailPlaceholder', lang)} value={email} onChange={(e) => setEmail(e.target.value)} className="border rounded px-3 py-2.5 text-sm focus:outline-none focus:border-primary-blue" required />
                            <input type="tel" placeholder={uiText('phoneOptionalPlaceholder', lang)} value={phone} onChange={(e) => setPhone(e.target.value)} className="border rounded px-3 py-2.5 text-sm focus:outline-none focus:border-primary-blue" />
                            <textarea placeholder={uiText('yourMessagePlaceholder', lang)} value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="border rounded px-3 py-2.5 text-sm focus:outline-none focus:border-primary-blue resize-none" required />
                            <button type="submit" disabled={submitting} className="bg-primary-orange text-white font-medium py-3 rounded shadow hover:shadow-lg disabled:opacity-60">
                                {submitting ? uiText('sendingText', lang) : uiText('sendMessageBtn', lang)}
                            </button>
                        </form>
                    </div>

                    <div className="flex flex-col gap-6">
                        <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">{uiText('getInTouch', lang)}</h2>
                            <div className="flex flex-col gap-3 text-sm">
                                <div>
                                    <p className="font-medium text-gray-700">{page.companyName}</p>
                                    <p className="text-gray-500">{displayAddress}</p>
                                </div>
                                {page.phone && <a href={`tel:${page.phone.replace(/\s/g, '')}`} className="text-primary-blue hover:underline">📞 {page.phone}</a>}
                                {page.whatsapp && <a href={`https://api.whatsapp.com/send?phone=${page.whatsapp.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-primary-blue hover:underline">💬 WhatsApp {page.whatsapp}</a>}
                                {page.email && <a href={`mailto:${page.email}`} className="text-primary-blue hover:underline">✉️ {page.email}</a>}
                                <div className="flex gap-3 mt-1">
                                    {page.facebook && <a href={page.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary-blue">Facebook</a>}
                                    {page.instagram && <a href={page.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary-blue">Instagram</a>}
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                            <iframe title="1313 Mart Location" src={mapEmbedSrc} width="100%" height="260" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                            <a href={mapDirectionsHref} target="_blank" rel="noopener noreferrer" className="block text-center text-sm text-primary-blue hover:underline py-3">{uiText('getDirections', lang)}</a>
                        </div>
                    </div>

                </div>
            </main>
        </>
    );
};

export default ContactUs;
